import * as React from 'react';
import { Empty, Select, Spinner } from '@revfluence/fresh';
import { useProjectBudgetContext } from '@frontend/app/context/ProjectBudgetContext';
import { useGetBudgetAccounts } from '@frontend/app/hooks/budgetAllocation/useGetBudgetAccounts';
import { omit } from 'lodash';
import { useGetBudgetDistributionsForBudgetAccount } from '@frontend/app/hooks/budgetAllocation/useGetBudgetDistributionsForBudgetAccount';
import { useGetBudgetFiscalPeriod } from '@frontend/app/hooks/budgetAllocation/useGetBudgetFiscalPeriod';
import { useGetProjectBudgetDashboardData } from '@frontend/app/hooks/budgetAllocation/useGetProjectBudgetDashboardData';
import { TBudgetFiscalPeriod } from '@frontend/applications/PaymentsApp/types';
import { Button } from '@frontend/shadcn/components/ui/button';
import { P } from '@frontend/shadcn/components/typography/p';
import { DashboardLayout } from '@frontend/app/refresh-components';
import widgetMapper from '@frontend/app/utils/widgetMapper';
import { useGetDashboardConfig } from '@frontend/app/hooks';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingColumns } from '@fortawesome/free-solid-svg-icons';
import ConnectBudgetAccountDrawer from './components/ConnectBudgetAccountDrawer';
import ConnectBudgetAccountModal from './components/ConnectBudgetAccountModal';
import { BudgetActionMode } from './types/IBudget';
import { SectionCard } from '../CampaignPage/components/SectionCard';

const { Option } = Select;

const { useEffect, useState } = React;
interface IProps {}

const ProjectBudgetFlex: React.FC<IProps> = () => {
  const [financialYear, setFinancialYear] = useState<TBudgetFiscalPeriod[]>([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState<TBudgetFiscalPeriod | null>(null);

  const {
    showModal,
    granularity,
    selectedBudgetId,
    projectId,
    mode,
    setBudgetGranularity,
    setShowModal,
    setShowDrawer,
    setBudgetAccounts,
    setBudgetAccountDistribution,
    setMode,
    setFiscalYearBudgetBreakDowns,
    setBudgetAccountDistributionConnectedAccount,
  } = useProjectBudgetContext();
  const { data: dashboardConfigData, loading: isDashboardConfigLoading } = useGetDashboardConfig();
  const { budgetAccounts: budgetAccountsForClient, loading: budgetsLoading } = useGetBudgetAccounts({});
  const { loading: budgetFiscalPeriodLoading, budgetFiscalPeriod } = useGetBudgetFiscalPeriod({});
  const {
    loading: loadingBudgetDistribution,
    budgetDistributionsForBudgetAccount,
  } = useGetBudgetDistributionsForBudgetAccount({
    variables: {
      budgetAccountId: selectedBudgetId,
    },
    skip: !selectedBudgetId,
    fetchPolicy: 'no-cache',
  });
  const {
    loading: loadingBudgetDistributionsForConnectedBudgetAccount,
    budgetDistributionsForBudgetAccount: budgetDistributionsForConnectedBudgetAccount,
  } = useGetBudgetDistributionsForBudgetAccount({
    variables: {
      budgetAccountId: selectedBudgetId,
      programId: parseInt(projectId, 10),
    },
    skip: !selectedBudgetId || !projectId,
    fetchPolicy: 'no-cache',
  });

  const {
    loading: loadingProjectBudgetDashboardData,
    refetch,
  } = useGetProjectBudgetDashboardData({
    skip: !selectedFinancialYear || !selectedFinancialYear.fiscalYear,
    variables:
      selectedFinancialYear && selectedFinancialYear.fiscalYear
        ? {
            programId: parseInt(projectId, 10),
            fiscalYear: selectedFinancialYear.fiscalYear,
          }
        : undefined,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!budgetFiscalPeriodLoading && budgetFiscalPeriod.length) {
      setFinancialYear(budgetFiscalPeriod);
      setSelectedFinancialYear(budgetFiscalPeriod.find((period) => period.isCurrentYear) || null);
    }
  }, [budgetFiscalPeriodLoading, budgetFiscalPeriod]);
  useEffect(() => {
    if (!budgetsLoading && budgetAccountsForClient.length) {
      const cleanedBudgetAccounts = budgetAccountsForClient.map((account) => omit(account, '__typename'));
      setBudgetAccounts(cleanedBudgetAccounts);
    }
  }, [budgetsLoading, budgetAccountsForClient, setBudgetAccounts]);
  useEffect(() => {
    setFiscalYearBudgetBreakDowns([]);
    if (!loadingBudgetDistribution && budgetDistributionsForBudgetAccount?.length) {
      setBudgetAccountDistribution(budgetDistributionsForBudgetAccount);
    } else {
      setBudgetAccountDistribution([]);
    }
    if (!loadingBudgetDistributionsForConnectedBudgetAccount && budgetDistributionsForConnectedBudgetAccount?.length) {
      setBudgetAccountDistributionConnectedAccount(budgetDistributionsForConnectedBudgetAccount);
    } else {
      setBudgetAccountDistributionConnectedAccount([]);
    }
  }, [
    loadingBudgetDistribution,
    budgetDistributionsForBudgetAccount,
    loadingBudgetDistributionsForConnectedBudgetAccount,
    budgetDistributionsForConnectedBudgetAccount,
    selectedBudgetId,
    setFiscalYearBudgetBreakDowns,
    setBudgetAccountDistribution,
    setBudgetAccountDistributionConnectedAccount,
  ]);

  const refreshData = () => {
    refetch();
  };
  const pageHeader = (
    <SectionCard
      icon={<FontAwesomeIcon icon={faBuildingColumns} />}
      title="Budget"
      subtitle="Budget"
      description="View and connect Budget Accounts for this project to track your member's payments over time."
      rightContent={
        <Button
          variant="outline"
          onClick={() => {
            setMode(BudgetActionMode.CREATE);
            setShowDrawer(true);
          }}
        >
          + Connect Budget
        </Button>
      }
    />
  );

  const section = dashboardConfigData?.dashboardConfig?.dashboardConfig?.find(
    (config) => config.name === 'project_budget_dashboard'
  ) || null;

  if (budgetsLoading || budgetFiscalPeriodLoading || loadingProjectBudgetDashboardData || isDashboardConfigLoading) {
    return <Spinner />;
  }
  if (!financialYear.length) {
    return (
      <div className="my-6 mx-12">
        <Empty description="Please setup budget fiscal year" />
      </div>
    );
  }
  const isSingleFiscalYear = financialYear.length === 1 && financialYear[0].fiscalYear.toString() === 'N/A';
  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4 my-6 mx-12">
        {pageHeader}
        <div className="flex flex-row justify-between items-center">
          <P className="text-base font-medium">Project Analysis</P>
          {!isSingleFiscalYear && selectedFinancialYear && (
            <div className="flex items-center gap-2">
              <Select
                value={selectedFinancialYear?.fiscalYear ?? ''}
                style={{ width: 120 }}
                onChange={(e) => setSelectedFinancialYear(financialYear.find((fy) => fy.fiscalYear === e))}
              >
                {financialYear.map((year) => (
                  <Option key={year.fiscalYear} value={year.fiscalYear}>
                    {year.fiscalYearLabel}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
        {section?.sections?.[0] && (
          <section className="mb-4">
            <DashboardLayout
              components={section.sections[0]?.widgets.map((widget) => ({
                content: widgetMapper({
                  type: widget.type,
                  props: {
                    projectId: parseInt(projectId, 10),
                    fiscalYear: selectedFinancialYear?.fiscalYear
                  }
                }),
                columns: widget.width,
              }))}
              gap="4"
            />
          </section>
        )}
        <ConnectBudgetAccountModal
          showModal={showModal}
          setShowModal={setShowModal}
          granularity={granularity}
          setBudgetGranularity={setBudgetGranularity}
          onOk={() => {
            setShowModal(false);
            setShowDrawer(true);
          }}
        />
        <ConnectBudgetAccountDrawer
          title={mode === BudgetActionMode.EDIT ? 'Edit Budget Account' : 'Connect Budget Account'}
          refreshData={refreshData}
          loading={loadingBudgetDistribution || loadingBudgetDistributionsForConnectedBudgetAccount}
          isSingleFiscalYear={isSingleFiscalYear}
        />
      </div>
    </TooltipProvider>
  );
};
export default ProjectBudgetFlex;
ProjectBudgetFlex.displayName = 'ProjectBudgetFlex';
